import React, { useEffect, useRef, useState } from 'react';
import { EdgeLinkBridgeDetail, generateEdgeLinkInstallCommand, useGenerateShareLink } from '@/lib/api/edgelink';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import LogViewer from '../components/LogViewer';
import { ArrowLeft, ArrowRight, Terminal, CheckCircle2, Share2, Loader2 } from 'lucide-react';
import { useEdgeLinkLogs } from '@/hooks/useEdgeLinkLogs';

interface ConnectionInstructionsStepProps {
  bridge: EdgeLinkBridgeDetail;
  onContinue: () => void;
  onBack: () => void;
}

export default function ConnectionInstructionsStep({
  bridge,
  onContinue,
  onBack,
}: ConnectionInstructionsStepProps) {
  const resolvedInstructions = generateEdgeLinkInstallCommand(bridge.authToken);
  const hasAutoAdvanced = useRef(false);
  const generateShareLink = useGenerateShareLink();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const shareLinkGenerated = useRef(false);

  // Auto-generate share link on mount (ref guard prevents duplicate calls on remount)
  useEffect(() => {
    if (shareLinkGenerated.current) return;
    shareLinkGenerated.current = true;

    generateShareLink.mutateAsync({
      bridgeId: bridge.summary.id,
      expiryDays: 30,
    }).then((result) => {
      setShareUrl(`${window.location.origin}/edgelink/s/${result.token}`);
    }).catch((err) => {
      console.warn('Failed to generate share link during creation flow:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridge.summary.id]);

  // Connect to log stream
  const { logs, isConnected } = useEdgeLinkLogs({
    bridgeId: bridge.summary.id,
    enabled: true,
  });

  // Auto-advance to verification when first log received
  useEffect(() => {
    if (logs.length > 0 && !hasAutoAdvanced.current) {
      hasAutoAdvanced.current = true;
      onContinue();
    }
  }, [logs.length, onContinue]);

  return (
    <div className="flex flex-col">
      <div className="p-6 space-y-6">
        {/* Success message */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-emerald-400">Bridge created successfully</p>
            <p className="text-sm text-emerald-300/70 mt-1">
              "{bridge.summary.name}" is ready. Run the command below to establish the connection.
            </p>
          </div>
        </div>

        {/* Instructions */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Terminal className="h-4 w-4 text-lime" />
            <span>Connection Command</span>
          </div>

          <div className="relative">
            <div className="bg-zinc-950 text-zinc-100 px-4 py-3 rounded-lg text-sm font-mono border border-zinc-800 pr-12 overflow-x-auto">
              <code className="whitespace-pre-wrap break-all leading-relaxed">
                {resolvedInstructions}
              </code>
            </div>
            <div className="absolute top-2 right-2">
              <CopyButton textToCopy={resolvedInstructions} label="Command" />
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Run this command as root on your server or device where you want to establish the EdgeLink
            tunnel. The tunnel will automatically connect to Cloudflare's network.
          </p>
        </div>

        {/* Share link */}
        {shareUrl && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Share2 className="h-4 w-4 text-lime" />
              <span>Share Status Link</span>
            </div>
            <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2">
              <span className="flex-1 text-xs font-mono text-zinc-300 truncate">
                {shareUrl}
              </span>
              <CopyButton textToCopy={shareUrl} label="Share Link" />
            </div>
            <p className="text-xs text-muted-foreground">
              Share this link to let anyone check the node's connection status.
            </p>
          </div>
        )}

        {/* Live logs */}
        <LogViewer logs={logs} isConnected={isConnected} compact maxHeight="120px" />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-[#2E3835] bg-card2/50">
        <Button type="button" variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button type="button" onClick={onContinue}>
          Continue
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
